// Prepare a picked photo for the AI vision endpoint (bag/menu OCR).
//
// Downscale to a max long edge and re-encode as JPEG. Two reasons:
//   1. Format — OpenAI's vision API accepts only JPEG/PNG/WEBP/GIF. iPhone HEIC
//      is rejected as-is; decoding to a canvas and re-encoding to JPEG fixes it
//      (browsers that can decode HEIC — notably Safari/iOS, where it comes from).
//   2. Payload — full-res phone photos bloat the base64 request body and can hit
//      Worker/OpenAI size limits when several are batched.
// Cost is NOT a motive: OpenAI downscales large images internally, so image
// tokens are already capped (~2048px long edge is at that ceiling → no OCR loss).
//
// These images only prefill the form; the archived R2 photo is uploaded
// separately from the original File, so this never degrades what's stored.

export interface UploadImage { imageBase64: string; mimeType: string; }

// Formats OpenAI's vision API accepts untouched (used as a decode fallback).
const OPENAI_OK = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export async function prepImageForUpload(
  file: File,
  maxPx = 2048,
  quality = 0.85,
): Promise<UploadImage> {
  try {
    const bmp = await decode(file);
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high"; // preserve small (CJK) label text on downscale
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    return { imageBase64: dataUrl.split(",", 2)[1], mimeType: "image/jpeg" };
  } catch {
    // Couldn't decode (e.g. HEIC in a browser without HEIC support). If the
    // original is a format OpenAI already accepts, send it untouched; otherwise
    // fail with a clear, actionable message instead of a doomed API request.
    if (OPENAI_OK.has(file.type)) {
      const dataUrl = await readAsDataURL(file);
      return { imageBase64: dataUrl.split(",", 2)[1], mimeType: file.type };
    }
    const kind = file.type || "this image format";
    throw new Error(
      `Couldn't read ${kind} in this browser (HEIC usually needs Safari). ` +
      "Export or share the photo as JPEG, then try again.",
    );
  }
}

// Decode a File to an ImageBitmap, baking in EXIF orientation so the re-encoded
// JPEG isn't rotated. Falls back to an <img> decode for older browsers.
async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error("decode failed"));
        i.src = url;
      });
      return await createImageBitmap(img);
    } finally { URL.revokeObjectURL(url); }
  }
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
